import { useEffect, useMemo, useState } from 'react';

export type Modules = {
  idp: boolean;
  pdp: boolean;
  workflow: boolean;
  crud: boolean;
  observability: boolean;
};

const defaultModules: Modules = { idp: true, pdp: true, workflow: true, crud: true, observability: true };

function parseEnvModules(): Modules | null {
  const raw = import.meta.env.VITE_EXPERIENCE_MODULES as string | undefined;
  if (!raw) return null;
  const set = new Set(raw.split(',').map(s => s.trim().toLowerCase()).filter(Boolean));
  return {
    idp: set.has('idp'),
    pdp: set.has('pdp'),
    workflow: set.has('workflow'),
    crud: set.has('crud'),
    observability: set.has('observability')
  };
}

export function useUiModules() {
  const [modules, setModules] = useState<Modules>(parseEnvModules() ?? defaultModules);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [themeMode, setThemeMode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let es: EventSource | null = null;
    (async () => {
      try {
        const csrf = (document.cookie.match(/(?:^|; )_eid_csrf_v1=([^;]+)/)?.[1]) || '';
        const resp = await fetch('/api/configs/ui', {
          credentials: 'include',
          headers: {
            'X-CSRF-Token': csrf,
            'X-CSRFToken': csrf,
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        if (!resp.ok) throw new Error('ui-config fetch failed');
        const data = await resp.json();
        if (!cancelled && data?.modules) setModules({ ...defaultModules, ...data.modules });
        // Apply theme tokens if provided via ThemeManager (highest priority)
        if (!cancelled && data?.theme) {
          import('../theme/ThemeManager').then(({ themeManager }) => {
            const vars = data.theme.cssVars || {};
            
            // Normalize CSS variable keys (remove -- prefix for ThemeManager)
            const normalizedVars: Record<string, string> = {};
            Object.entries(vars).forEach(([k, v]) => {
              const key = k.startsWith('--') ? k.slice(2) : k;
              normalizedVars[key] = String(v);
            });
            
            themeManager.setColors('live-config', normalizedVars);
          });
          
          if (data.theme.mode) {
            setThemeMode(String(data.theme.mode));
            document.documentElement.setAttribute('data-theme', String(data.theme.mode));
          }
          // Optional brand pack CSS
          const brandCss: string | undefined = (data.theme as any).brandCssUrl;
          if (brandCss) {
            let link = document.getElementById('tenant-brand-css') as HTMLLinkElement | null;
            if (!link) {
              link = document.createElement('link');
              link.id = 'tenant-brand-css';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
            link.href = brandCss;
          }
        }
      } catch {
        // ignore: fallback to env/default
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Subscribe to live config/theme updates
    try {
      es = new EventSource('/api/configs/stream', { withCredentials: true } as any);
      es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data?.modules) setModules({ ...defaultModules, ...data.modules });
          if (data?.theme) {
            import('../theme/ThemeManager').then(({ themeManager }) => {
              const vars = data.theme.cssVars || {};
              
              // Normalize CSS variable keys (remove -- prefix for ThemeManager)
              const normalizedVars: Record<string, string> = {};
              Object.entries(vars).forEach(([k, v]) => {
                const key = k.startsWith('--') ? k.slice(2) : k;
                normalizedVars[key] = String(v);
              });
              
              themeManager.setColors('live-config', normalizedVars);
            });
            
            if (data.theme.mode) {
              setThemeMode(String(data.theme.mode));
              document.documentElement.setAttribute('data-theme', String(data.theme.mode));
            }
            const brandCss: string | undefined = (data.theme as any).brandCssUrl;
            if (brandCss) {
              let link = document.getElementById('tenant-brand-css') as HTMLLinkElement | null;
              if (!link) {
                link = document.createElement('link');
                link.id = 'tenant-brand-css';
                link.rel = 'stylesheet';
                document.head.appendChild(link);
              }
              link.href = brandCss;
            }
          }
        } catch { /* ignore */ }
      };
    } catch { /* ignore */ }
    return () => { cancelled = true; };
  }, []);

  return useMemo(() => ({ modules, isLoading, themeMode }), [modules, isLoading, themeMode]);
}


